import { flatten } from 'lodash';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { DnaEpic } from '../../typings/epic';
import { getSelectedBatches } from '../BatchesList/helpers';
import { findItem } from '../common/helpers';
import { UploadHtml5Response } from '../common/requestsTypings';
import { UploadStatus } from '../Html5Upload/typings';

import {
    createCreative,
    createCreativeSuccess,
    deleteCreative,
    deleteCreativeSuccess,
    Html5Upload,
    LoadItems,
    loadItems,
    loadItemsSuccess,
    SaveName,
    saveName,
    saveNameSuccess,
    showError,
    Submit,
    submit,
    upload,
    uploadFinished
} from './actions';
import { createCreativesCollection, createUploadCreative } from './mappers';
import { getCurrentCreativesState } from './selectors';
import { Html5Action, Html5Page } from './typings';

export const loadHtml5Creatives: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(loadItems)),
        switchMap(
            (action: LoadItems): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;
                const { page } = canvasEditor.html5;
                const { creativeSizes } = getCurrentCreativesState(canvasEditor.html5).filter;
                const { offset, order, sizes, filterText } = action;
                const getParams = {
                    offset,
                    order,
                    sizes,
                    filterText,
                    archived: page === Html5Page.ARCHIVE
                };

                return from(deps.canvasApi.html5.getCreatives(getParams, sharedData)).pipe(
                    map(result => loadItemsSuccess(createCreativesCollection(result), creativeSizes)),
                    catchError(error => of(showError(error.message || error.getNativeError().message)))
                );
            }
        )
    );
};

export const saveHtml5CreativeName: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(saveName)),
        switchMap(
            (action: SaveName): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;
                const { collection } = getCurrentCreativesState(canvasEditor.html5).batchesList;
                const batch = findItem(action.id, collection.batches);
                const oldName = batch.name;
                const newName = action.val ? action.val.trim() : '';
                const id = action.id;

                if (!newName || newName === oldName) {
                    return of(saveNameSuccess(id, oldName));
                }

                return from(deps.canvasApi.html5.update(id, newName, sharedData)).pipe(
                    map(() => saveNameSuccess(id, newName)),
                    catchError(error => of(showError(error.message || error.getNativeError().message)))
                );
            }
        )
    );
};

export const deleteHtml5Creative: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(deleteCreative)),
        switchMap(
            (): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;
                const { idToDelete } = canvasEditor.html5.activeCreatives.batchesList;

                return from(deps.canvasApi.html5.delete(idToDelete, sharedData)).pipe(
                    map(() => deleteCreativeSuccess(idToDelete)),
                    catchError(error => of(showError(error.message || error.getNativeError().message)))
                );
            }
        )
    );
};

export const submitHtml5Creatives: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(submit)),
        switchMap(
            (action: Submit): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;
                const { collection } = canvasEditor.html5.activeCreatives.batchesList;

                const selectedBatches = getSelectedBatches(collection);

                return from(deps.canvasApi.html5.submit(selectedBatches, sharedData)).pipe(
                    mergeMap(() => {
                        const ids = flatten(selectedBatches.map(batch => batch.creativeIds));
                        action.callback(ids);

                        return EMPTY;
                    }),
                    catchError(error => of(showError(error.message || error.getNativeError().message)))
                );
            }
        )
    );
};

export const uploadHtml5Creative: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(upload)),
        mergeMap(
            (action: Html5Upload): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;

                return from(action.creatives).pipe(
                    mergeMap(creative => {
                        if (!creative.file) {
                            throw new Error('Nothing to upload');
                        }

                        return from(deps.canvasApi.html5.upload(creative.file, sharedData)).pipe(
                            map((resp: UploadHtml5Response) => uploadFinished(createUploadCreative(creative, resp))),
                            catchError(error => of(showError(error.message || error.getNativeError().message)))
                        );
                    })
                );
            }
        )
    );
};

export const createHtml5Creative: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(createCreative)),
        switchMap(
            (): Observable<Html5Action> => {
                const { canvasEditor } = store$.value;
                const sharedData = canvasEditor.sharedData;
                const { name, creatives } = canvasEditor.html5.uploadCreatives;
                const creativesArray = Object.keys(creatives).map(id => creatives[id]);
                const ids = creativesArray
                    .filter(creative => creative.status === UploadStatus.SUCCESS)
                    .map(creative => creative.id);

                return from(deps.canvasApi.html5.create(name, ids, sharedData)).pipe(
                    map(() => createCreativeSuccess()),
                    catchError(error => of(showError(error.message || error.getNativeError().message)))
                );
            }
        )
    );
};

export const html5Epics = combineEpics(
    loadHtml5Creatives,
    saveHtml5CreativeName,
    deleteHtml5Creative,
    uploadHtml5Creative,
    submitHtml5Creatives,
    createHtml5Creative
);
